import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import { getSupabase } from "../utils";
import type { User } from "@/lib/types/user";

/** Minimal role row read from the `roles` table for name resolution. */
interface RoleRow {
  id: string;
  name?: string | null;
  description?: string | null;
}

/** Row returned by the `search_app_users` FTS endpoint. */
interface AdminUserSearchRow {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  role_id?: string | null;
}

// ============================================================================
// ADMIN USERS — READ HOOKS (queries)
// ============================================================================

/**
 * Fetch all admin users with optional filtering.
 * Uses server-side FTS search when a search term is provided.
 */
export function useAdminUsers(params?: {
  search?: string;
  role?: string;
  is_active?: boolean;
  page?: number;
  pageSize?: number;
}) {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const supabase = getSupabase();

  return useQuery({
    queryKey: ["admin-users", params],
    queryFn: async (): Promise<User[]> => {
      if (!isAuthenticated || !appUser) {
        throw new Error("Authentication required");
      }

      const userRole = appUser.roles?.name || appUser.role;
      if (!["wtc_employee", "wtc_admin", "wtc_crm"].includes(userRole || "")) {
        throw new Error("Insufficient permissions");
      }

      // Server-side FTS search when a search term is provided
      if (params?.search && params.search.trim().length > 0) {
        const { data } = await supabase.auth.getSession();
        const session = data?.session;
        if (!session?.access_token) {
          throw new Error("Not authenticated");
        }

        const response = await fetch("/api/search/admin_users", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            query: params.search.trim(),
            limit: params?.pageSize || 1000,
            offset:
              params?.page && params?.pageSize
                ? params.page * params.pageSize
                : 0,
          }),
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.statusText}`);
        }

        const searchData = await response.json();
        if (!searchData.success) {
          throw new Error(searchData.error || "Search failed");
        }

        // search_app_users() returns role_id but not the role name, so resolve
        // names from the roles table (tiny — 4 rows) and attach the `roles`
        // relation the UI reads for the role column.
        const { data: rolesData } = await supabase
          .from("roles")
          .select("id, name, description");
        const roleById = new Map<string, RoleRow>(
          ((rolesData as RoleRow[] | null) || []).map((r) => [r.id, r])
        );

        // Transform search results to match User type
        const searchRows = (searchData.data || []) as AdminUserSearchRow[];
        const results: User[] = searchRows.map((result) => ({
          id: result.id,
          first_name: result.first_name ?? undefined,
          last_name: result.last_name ?? undefined,
          email: result.email ?? undefined,
          is_active: result.is_active,
          created_at: result.created_at ?? undefined,
          role_id: result.role_id ?? undefined,
          roles: result.role_id
            ? (roleById.get(result.role_id) as User["roles"])
            : undefined,
        }));

        let filtered = results;
        if (params?.is_active !== undefined) {
          filtered = filtered.filter((u) => u.is_active === params.is_active);
        }
        if (params?.role) {
          filtered = filtered.filter((u) => u.roles?.name === params.role);
        }

        return filtered;
      }

      // Standard REST API query
      let query = supabase
        .from("app_users")
        .select(
          `
          *,
          roles:role_id (
            id,
            name,
            description
          )
        `
        )
        .order("created_at", { ascending: false });

      if (params?.is_active !== undefined) {
        query = query.eq("is_active", params.is_active);
      }
      if (params?.role) {
        query = query.eq("roles.name", params.role);
      }
      if (params?.pageSize && params?.page !== undefined) {
        const offset = params.page * params.pageSize;
        query = query.range(offset, offset + params.pageSize - 1);
      }

      const { data, error } = await query;
      if (error) {
        throw new Error(`Failed to fetch users: ${error.message}`);
      }

      return data || [];
    },
    enabled: isAuthenticated && !!appUser,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a single user by ID.
 */
export function useAdminUser(userId: string) {
  const { appUser, isAuthenticated } = useUniversalAuth();
  const supabase = getSupabase();

  return useQuery({
    queryKey: ["admin-user", userId],
    queryFn: async (): Promise<User> => {
      if (!isAuthenticated || !appUser) {
        throw new Error("Authentication required");
      }

      const { data, error } = await supabase
        .from("app_users")
        .select(
          `
          *,
          roles:role_id (
            id,
            name,
            description
          )
        `
        )
        .eq("id", userId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch user: ${error.message}`);
      }

      return data;
    },
    enabled: isAuthenticated && !!appUser && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
